import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CreditCard, Check, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useGetPaymentConfig,
  useUpsertPaymentConfig,
  SECRET_KEEP_SENTINEL,
  type PaymentConfigInput,
} from '@/services/api/endpoints/CompanyConfigService';

interface Props {
  companyId: string;
}

/**
 * Secrets are write-only — the backend never returns them. The UI shows
 * "Configured ✓" badges instead. When the user submits without typing into a
 * secret field, we send a sentinel so the backend leaves the prior value
 * untouched. Clearing requires explicitly emptying the field.
 */
export function CompanyPaymentConfigTab({ companyId }: Props) {
  const { t } = useTranslation();
  const { data: config, isLoading } = useGetPaymentConfig(companyId);
  const upsert = useUpsertPaymentConfig(companyId);

  const [enabled, setEnabled] = useState(false);
  const [payspotCompanyId, setPayspotCompanyId] = useState('');
  const [payspotShopId, setPayspotShopId] = useState('');
  const [payspotMitShopId, setPayspotMitShopId] = useState('');
  const [payspotMitTerminalId, setPayspotMitTerminalId] = useState('');
  const [payspotBaseUrl, setPayspotBaseUrl] = useState('');
  // Secrets are blank by default; sending blank = clear, sentinel = keep.
  const [payspotSecretKey, setPayspotSecretKey] = useState('');
  const [payspotCallbackSecretKey, setPayspotCallbackSecretKey] = useState('');
  const [secretKeyTouched, setSecretKeyTouched] = useState(false);
  const [callbackSecretTouched, setCallbackSecretTouched] = useState(false);

  useEffect(() => {
    if (!config) return;
    setEnabled(config.enabled);
    setPayspotCompanyId(config.payspotCompanyId ?? '');
    setPayspotShopId(config.payspotShopId ?? '');
    setPayspotMitShopId(config.payspotMitShopId ?? '');
    setPayspotMitTerminalId(config.payspotMitTerminalId ?? '');
    setPayspotBaseUrl(config.payspotBaseUrl ?? '');
    setPayspotSecretKey('');
    setPayspotCallbackSecretKey('');
    setSecretKeyTouched(false);
    setCallbackSecretTouched(false);
  }, [config]);

  const handleSave = async () => {
    const body: PaymentConfigInput = {
      enabled,
      payspotCompanyId: payspotCompanyId.trim() || null,
      payspotShopId: payspotShopId.trim() || null,
      payspotMitShopId: payspotMitShopId.trim() || null,
      payspotMitTerminalId: payspotMitTerminalId.trim() || null,
      payspotBaseUrl: payspotBaseUrl.trim() || null,
      payspotSecretKey: secretKeyTouched ? payspotSecretKey : SECRET_KEEP_SENTINEL,
      payspotCallbackSecretKey: callbackSecretTouched ? payspotCallbackSecretKey : SECRET_KEEP_SENTINEL,
    };

    try {
      await upsert.mutateAsync(body);
      toast.success(t('payments.paymentConfigSaved'));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('payments.savingFailed'));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10" />
        <Skeleton className="h-32" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" />
              {t('payments.paymentConfigTitle')}
            </CardTitle>
            <CardDescription>{t('payments.paymentConfigDescription')}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={enabled} onCheckedChange={setEnabled} />
            <span className="text-sm text-muted-foreground">
              {enabled ? t('common.active') : t('common.inactive')}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field
            label={t('payments.fields.payspotCompanyId')}
            placeholder="e.g. 12345"
            value={payspotCompanyId}
            onChange={setPayspotCompanyId}
          />
          <Field
            label={t('payments.fields.payspotBaseUrl')}
            placeholder="https://test.nsgway.rs:50009"
            value={payspotBaseUrl}
            onChange={setPayspotBaseUrl}
          />
          <Field
            label={t('payments.fields.payspotShopId')}
            placeholder="e.g. shop_001"
            value={payspotShopId}
            onChange={setPayspotShopId}
          />
          <Field
            label={t('payments.fields.payspotMitShopId')}
            placeholder={t('payments.fields.mitShopIdHint')}
            value={payspotMitShopId}
            onChange={setPayspotMitShopId}
          />
          <Field
            label={t('payments.fields.payspotMitTerminalId')}
            placeholder={t('payments.fields.mitTerminalIdHint')}
            value={payspotMitTerminalId}
            onChange={setPayspotMitTerminalId}
          />
        </div>

        <SecretField
          label={t('payments.fields.payspotSecretKey')}
          status={config?.hasSecretKey}
          value={payspotSecretKey}
          touched={secretKeyTouched}
          onChange={(v) => {
            setPayspotSecretKey(v);
            setSecretKeyTouched(true);
          }}
        />
        <SecretField
          label={t('payments.fields.payspotCallbackSecretKey')}
          status={config?.hasCallbackSecretKey}
          value={payspotCallbackSecretKey}
          touched={callbackSecretTouched}
          onChange={(v) => {
            setPayspotCallbackSecretKey(v);
            setCallbackSecretTouched(true);
          }}
        />

        <div className="flex justify-end pt-2">
          <Button onClick={handleSave} disabled={upsert.isPending}>
            {upsert.isPending ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

function SecretField({
  label,
  status,
  value,
  touched,
  onChange,
}: {
  label: string;
  status: boolean | undefined;
  value: string;
  touched: boolean;
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <span
          className={`text-xs flex items-center gap-1 ${
            status ? 'text-green-600' : 'text-muted-foreground'
          }`}
        >
          {status ? (
            <>
              <Check className="w-3 h-3" /> {t('payments.secretConfigured')}
            </>
          ) : (
            <>
              <X className="w-3 h-3" /> {t('payments.secretNotSet')}
            </>
          )}
        </span>
      </div>
      <Input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={status && !touched ? t('payments.fields.secretKeep') : t('payments.fields.secretEnter')}
      />
      {touched && value === '' && (
        <p className="text-xs text-amber-600">{t('payments.fields.secretWillClear')}</p>
      )}
    </div>
  );
}
